import { Navigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useCurrentUser } from "@/hooks/useAuth";
import { useProject } from "@/hooks/useApi";
import { statusOf } from "@/lib/api-error";
import { isAreaAvailable } from "@/lib/projectSource";
import {
  PROJECT_ROUTE_AREA,
  canReachProjectRoute,
  projectHomePath,
  type ProjectRouteSlug,
} from "@/lib/project-nav";

/**
 * As respostas que significam "não há projeto aqui" — e não "tente de novo".
 *
 * - **`404`** é a recusa por alcance, e é **opaca de propósito**: o backend
 *   responde o mesmo para projeto inexistente e para projeto de outra pessoa,
 *   para não revelar que ele existe (`require_project_access`).
 * - **`403`** é a recusa por papel, que o catálogo já cobre daqui. Está na lista
 *   porque uma rota que o backend feche sem a tela saber não pode virar
 *   esqueleto eterno.
 * - **`422`** é o id que **nem é UUID**. O handler declara `project_id: UUID`, e
 *   o FastAPI valida o path param antes de rodar qualquer código — um id
 *   truncado ou com caractere não-hexadecimal nunca chega a virar 404. Foi
 *   exatamente o caso que escapou da primeira versão deste guarda: URL colada
 *   pela metade dá 422, e a tela ficava carregando para sempre. Há teste no
 *   backend pinando esse contrato (`TestMalformedProjectId`).
 *
 * `500` e falha de rede ficam **fora**: elas não dizem nada sobre alcance.
 * Expulsar alguém do projeto por um soluço de conexão é um bug pior que o que
 * este guarda conserta, e sem nenhuma pista para quem o vive.
 */
const UNREACHABLE_STATUSES = [403, 404, 422];

function unreachable(error: unknown): boolean {
  const status = statusOf(error);
  return status != null && UNREACHABLE_STATUSES.includes(status);
}

/**
 * O id tem forma de UUID? Espelha `uuid.UUID` do Python, que é quem decide.
 *
 * Serve para responder **sem sair do navegador** ao caso mais comum de URL
 * errada: id colado pela metade, ou digitado à mão. Esperar o `422` do servidor
 * também funciona, só que custa uma ida e volta — e nessa ida e volta a tela
 * monta, dispara as consultas dela, e o grupo "This Project" pisca na barra
 * lateral, que é montada a partir do endereço e não espera resposta nenhuma.
 *
 * A regra é **igual à do servidor**, não mais rígida: tira `urn:`/`uuid:`, tira
 * chaves e hífens, e exige 32 dígitos hexadecimais. Uma versão mais severa aqui
 * (exigir hífen na posição certa, cobrar a versão do UUID) criaria o defeito
 * inverso — a tela recusando um id que a API atenderia. Este guarda existe para
 * concordar com o backend, não para ter opinião própria.
 */
function looksLikeUuid(id: string): boolean {
  const hex = id.replace("urn:", "").replace("uuid:", "").replace(/^\{|\}$/g, "").replace(/-/g, "");
  return /^[0-9a-fA-F]{32}$/.test(hex);
}

interface ProjectRouteGuardProps {
  /** A tela desta rota. O papel mínimo vem do catálogo, não do `App.tsx`. */
  slug: ProjectRouteSlug;
  children: React.ReactNode;
}

/**
 * Barra as telas de projeto que o papel de quem olha não alcança.
 *
 * Cobre **toda** rota debaixo de `/projects/:projectId` — as sete abas da sidebar
 * e também os workbenches de agente e o Code Companion, que não são abas mas têm
 * URL própria e por isso abriam com um link colado.
 *
 * Espera a sessão já resolvida: as rotas de projeto ficam dentro do `AuthGuard`
 * que envolve o `AppLayout`, e a única que fica fora (`agents/functional`) tem um
 * `AuthGuard` próprio por volta. **Da sessão** não há carregando para tratar aqui
 * — o do projeto, sim, e é tratado abaixo.
 *
 * Por que um componente próprio em vez de `<AuthGuard minRole="consultant">`:
 *
 * - o mínimo de cada tela precisa morar em **um** lugar. Repetido nas rotas, ele
 *   divergiria da sidebar no primeiro ajuste — e a divergência é silenciosa: o
 *   link some e a URL continua abrindo, ou o contrário;
 * - a Overview tem destino diferente. Ela é o alvo de **todo** link "abrir
 *   projeto" da aplicação (lista, dashboard, retorno do ADT). Mandá-la
 *   para `/projects` faria clicar num projeto devolver a pessoa à lista.
 *
 * Guardar na rota, e não dentro de cada página, é o que evita montar a tela,
 * disparar as chamadas e só então descobrir que não era para estar ali.
 */
export function ProjectRouteGuard({ slug, children }: ProjectRouteGuardProps) {
  const me = useCurrentUser();
  const { projectId } = useParams<{ projectId: string }>();
  const roleAllows = canReachProjectRoute(me?.role, slug);
  const malformedId = projectId != null && !looksLikeUuid(projectId);

  /**
   * O projeto em si, para saber se ele **existe para esta pessoa**.
   *
   * Mesma chave de query que a tela lá dentro já usa (`["project", id]`), então
   * isto não é uma requisição a mais: é uma segunda assinatura do mesmo cache.
   *
   * O `projectId` só é passado quando vale perguntar. Barrado por papel, não há
   * motivo para consultar o servidor sobre um projeto que não será aberto; e com
   * id malformado a resposta já é conhecida — seria pagar uma ida e volta para
   * receber o `422` que a forma do id já anuncia.
   */
  const askTheServer = roleAllows && !malformedId;
  const { data: project, error, isLoading } = useProject(askTheServer ? projectId : undefined);

  /**
   * Resposta em voo **não** é permissão para entrar.
   *
   * Enquanto ela não volta, `error` é `undefined` e `unreachable` é falso — o que
   * fazia os filhos renderizarem. Colar `/projects/<uuid-inalcançável>/runs`
   * montava a tela, ela disparava as consultas dela, todas recusadas, e só então
   * o guarda navegava: exatamente o leque que este arquivo existe para não deixar
   * acontecer. O `malformedId` já era barrado assim; o pendente passou.
   *
   * ⚠️ **`isLoading`, nunca `isPending`.** Query desabilitada — papel barrado ou
   * id malformado — fica com status `pending` para sempre no TanStack v5, e com
   * `isPending` o guarda travaria no spinner justo nos dois caminhos que hoje
   * redirecionam certo. `isLoading` é `isPending && isFetching`, então só é
   * verdadeiro quando ele de fato perguntou. O `askTheServer` na condição é
   * redundante por isso mesmo, e fica: torna a intenção legível sem depender de
   * quem lê conhecer a semântica do hook.
   *
   * A espera custa uma ida e volta, e só na primeira entrada no projeto: a chave
   * é a mesma da tela e o `staleTime` do cache é de 30s. As rotas de projeto
   * ficam dentro do `AppLayout`, então a casca e a sidebar seguem na tela — daí o
   * spinner centrado no conteúdo, e não o de tela cheia do `AuthGuard`.
   */
  if (askTheServer && isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#7b8799]" />
      </div>
    );
  }

  const noSuchProject = malformedId || unreachable(error);

  /**
   * #1784: workbench de área que a origem do projeto não tem (Architect, Configuration Server e
   * Code Companion fora do SAP) volta para a visão geral do mesmo projeto, onde o pipeline já não
   * mostra o card. A lista seria o destino errado: o projeto existe e a pessoa alcança.
   */
  const area = PROJECT_ROUTE_AREA[slug];
  if (roleAllows && !noSuchProject && area && project && !isAreaAvailable(project, area)) {
    return <Navigate to={`/projects/${projectId}`} replace />;
  }

  if (roleAllows && !noSuchProject) {
    return <>{children}</>;
  }

  /**
   * Projeto inalcançável vai **sempre** para a lista, mesmo sendo a Overview.
   * Mandá-la para outra aba do mesmo projeto seria um laço: a aba de destino
   * pergunta pelo mesmo projeto e toma a mesma recusa.
   *
   * Barrado só por papel, a Overview mantém o destino de sempre — a primeira aba
   * que a pessoa alcança (o Diagnóstico, para um `user`) —, porque ela é o alvo
   * de todo link "abrir projeto" da aplicação. As demais vão para a lista, que é
   * o que se espera de uma URL digitada à mão.
   */
  const blockedByRoleOnOverview = !noSuchProject && slug === "" && projectId;
  const destination = blockedByRoleOnOverview
    ? projectHomePath(me?.role, projectId)
    : "/projects";

  return <Navigate to={destination} replace />;
}
